/**
 * Fixed 12-colour base palette + graphite (brief 10-colours, extended by
 * brief 11-layout2 §3) — "the 12 colours are the available pencils/chalks in
 * the Escherllate sketchbook." Evenly spaced round the hue wheel (30° apart)
 * so 2/3/4/6-way colour symmetry always lands on another member of the same
 * set (opposites/triads/squares/hexagonal relationships) rather than an
 * arbitrarily generated hue — "I pick a pencil colour; Escherllate handles
 * the harmony."
 *
 * A stroke stores only which of the 13 swatches it was drawn with (its
 * base-palette index); colour symmetry (off/2/3/4/6) is a rendering
 * transform over that index, not stored per stroke — see
 * paletteIndicesFor. Graphite (GRAPHITE_INDEX) is the one exception: it
 * sits outside the 12-colour ring and never rotates under any symmetry.
 */
import { hexToHue, hslToHex } from './colourGroups';

export const BASE_PALETTE_SIZE = 12;
/** The 13th swatch — graphite, centre of the ring, outside the rotating
 *  12-hue cycle (brief 11-layout2 §3). */
export const GRAPHITE_INDEX = BASE_PALETTE_SIZE;
/** Default selected swatch on first open / new sketch: graphite — "the
 *  product is a sketchbook; first touch should be pencil-on-paper, not an
 *  arbitrary hue." */
export const DEFAULT_COLOUR_INDEX = GRAPHITE_INDEX;

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/**
 * Light = graphite/coloured pencil on paper (soft, slightly desaturated);
 * dark = chalk/pastel on a dark board (lifted lightness, a touch more
 * saturated so it still reads as vivid on a dark ground). Hue is never
 * touched — only this saturation/lightness pair shifts between media (brief
 * 10-colours' "Dark mode" section: "do not invert hue... preserve hue
 * identity"). Tunable defaults — confirm/adjust on-device.
 */
const LIGHT_TONE = { s: 0.46, l: 0.44 };
const DARK_TONE = { s: 0.6, l: 0.7 };

/**
 * Graphite has no hue, so "preserve hue identity" doesn't apply — brief
 * 11-layout2 §3 makes it the deliberate exception to "never invert": dark
 * pencil-on-light-paper has no chalk equivalent at the same luminosity, so
 * dark mode inverts it toward a light chalk/white pastel instead. Light
 * value matches the pre-existing SKETCH_GRAPHITE tone (stores/tools.ts) so
 * picking the swatch looks identical to the old always-graphite default.
 */
const GRAPHITE_LIGHT = '#2a2a28';
const GRAPHITE_DARK = '#f2efe6';

function hueFor(index: number): number {
  return (360 / BASE_PALETTE_SIZE) * mod(index, BASE_PALETTE_SIZE);
}

/** The rendered hex for base-palette index `i` under the current medium — the
 *  stored index never changes, only this. */
export function paletteHex(index: number, isDark: boolean): string {
  if (index === GRAPHITE_INDEX) return isDark ? GRAPHITE_DARK : GRAPHITE_LIGHT;
  const tone = isDark ? DARK_TONE : LIGHT_TONE;
  return hslToHex(hueFor(index), tone.s, tone.l);
}

/**
 * Which base-palette indices render for a given base colour under n-way
 * colour symmetry (n must divide 12 — off/2/3/4/6 all do). E.g. base index 0
 * ("c1"), n=3 → [0, 4, 8] ("c1 renders as c1, c5, c9" — brief's worked
 * example). n<=1 (off) is just the base colour itself. Graphite is exempt —
 * it renders as graphite at every copy, regardless of n (brief 11-layout2
 * §3: "the one deliberate exception to 'colour symmetry is a rendering
 * transform based on the 12-colour cycle'").
 */
export function paletteIndicesFor(baseIndex: number, n: number): number[] {
  if (baseIndex === GRAPHITE_INDEX) return Array.from({ length: Math.max(1, n) }, () => GRAPHITE_INDEX);
  if (n <= 1) return [mod(baseIndex, BASE_PALETTE_SIZE)];
  const step = BASE_PALETTE_SIZE / n;
  return Array.from({ length: n }, (_, i) => mod(baseIndex + i * step, BASE_PALETTE_SIZE));
}

/** The N rendered hexes for a base colour under n-way colour symmetry. */
export function paletteColourSet(baseIndex: number, n: number, isDark: boolean): string[] {
  return paletteIndicesFor(baseIndex, n).map((i) => paletteHex(i, isDark));
}

/**
 * Reverse of `paletteHex` — recovers which swatch index a resolved hex came
 * from. Used only to migrate pre-swatch-index saved sketches (a stroke used
 * to freeze `paletteHex(index, isDark)` directly; this recovers `index` on
 * load so it can be re-frozen the current way). Lossless: hue is never
 * touched between light/dark (see LIGHT_TONE/DARK_TONE above), so every hex
 * this app could ever have produced round-trips exactly to its 30°-spaced
 * hue bucket; graphite is matched by exact value since it has no hue.
 */
export function nearestIndexForHex(hex: string): number {
  const lower = hex.toLowerCase();
  if (lower === GRAPHITE_LIGHT || lower === GRAPHITE_DARK) return GRAPHITE_INDEX;
  return mod(Math.round(hexToHue(hex) / (360 / BASE_PALETTE_SIZE)), BASE_PALETTE_SIZE);
}
